import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getData } from "../../utils/api";

function formatDateTimeIST(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        status === "success"
          ? "bg-green-100 text-green-800"
          : "bg-red-100 text-red-800"
      }`}
    >
      {status === "success" ? "Success" : "Failed"}
    </span>
  );
}

const CronLogs = () => {
  const navigate = useNavigate();
  const [serviceKey, setServiceKey] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const limit = 20;

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (serviceKey) qs.set("serviceKey", serviceKey);
      if (status) qs.set("status", status);
      const response = await getData(`/data/polling/cron-logs?${qs.toString()}`);
      if (response?.status && response?.data) {
        setResult(response.data);
      } else {
        setError(response?.message || "Failed to load cron logs");
      }
    } catch (err) {
      setError(err?.message || "Failed to load cron logs");
    } finally {
      setLoading(false);
    }
  }, [page, serviceKey, status]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const summary = result?.summary || [];
  const rows = result?.rows || [];
  const totalPages = result?.totalPages || 1;

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <button
          type="button"
          onClick={() => navigate("/analytics/polling-status")}
          className="text-gray-600 hover:text-gray-900 flex items-center gap-1"
        >
          ← Back to Polling Status
        </button>
        <button
          type="button"
          onClick={loadLogs}
          disabled={loading}
          className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <h1 className="text-2xl font-bold mb-2">Cron Logs</h1>
      <p className="text-sm text-gray-600 mb-6">
        Every success and failure of the polling crons. Entries older than 30
        days are removed automatically.
      </p>

      <h2 className="text-lg font-semibold mb-3">Latest status per cron</h2>
      <div className="overflow-x-auto rounded border border-gray-200 mb-8">
        <table className="min-w-full bg-white text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-3 py-2 text-left font-medium text-gray-700">Cron</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Last success</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Last failure</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Last error</th>
            </tr>
          </thead>
          <tbody>
            {summary.map((item) => {
              const failingNow =
                item.lastFailureAt &&
                (!item.lastSuccessAt ||
                  new Date(item.lastFailureAt) > new Date(item.lastSuccessAt));
              return (
                <tr
                  key={item.serviceKey}
                  className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                    failingNow ? "bg-red-50" : ""
                  }`}
                  onClick={() => {
                    setServiceKey(item.serviceKey);
                    setPage(1);
                  }}
                  title="Show logs for this cron"
                >
                  <td className="px-3 py-2 font-medium text-gray-900">{item.service}</td>
                  <td className="px-3 py-2 text-green-700">
                    {formatDateTimeIST(item.lastSuccessAt)}
                  </td>
                  <td className="px-3 py-2 text-red-700">
                    {formatDateTimeIST(item.lastFailureAt)}
                  </td>
                  <td className="px-3 py-2 text-gray-700 max-w-md truncate">
                    {item.lastError || "—"}
                  </td>
                </tr>
              );
            })}
            {!loading && summary.length === 0 && (
              <tr>
                <td colSpan="4" className="px-3 py-4 text-center text-gray-500">
                  No cron runs logged yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h2 className="text-lg font-semibold mr-2">Run history</h2>
        <select
          value={serviceKey}
          onChange={(e) => {
            setServiceKey(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
        >
          <option value="">All crons</option>
          {summary.map((item) => (
            <option key={item.serviceKey} value={item.serviceKey}>
              {item.service}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
        >
          <option value="">All statuses</option>
          <option value="failed">Failed</option>
          <option value="success">Success</option>
        </select>
      </div>

      {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

      <div className="overflow-x-auto rounded border border-gray-200 mb-4">
        <table className="min-w-full bg-white text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-3 py-2 text-left font-medium text-gray-700">Time (IST)</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Cron</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Consecutive failures</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Email sent</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Error</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-3 py-6 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-3 py-6 text-center text-gray-500">
                  No logs found.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <React.Fragment key={row.id}>
                  <tr
                    className={`border-b border-gray-100 ${
                      row.status === "failed" ? "cursor-pointer hover:bg-gray-50" : ""
                    }`}
                    onClick={() =>
                      row.status === "failed" &&
                      setExpandedId(expandedId === row.id ? null : row.id)
                    }
                  >
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                      {formatDateTimeIST(row.createdAt)}
                    </td>
                    <td className="px-3 py-2 text-gray-900 whitespace-nowrap">{row.service}</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {row.consecutiveFailures ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {row.emailSent === null || row.emailSent === undefined
                        ? "—"
                        : row.emailSent
                          ? "Yes"
                          : "No"}
                    </td>
                    <td className="px-3 py-2 text-red-700 max-w-md truncate">
                      {row.errorMessage || "—"}
                    </td>
                  </tr>
                  {expandedId === row.id && (
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <td colSpan="6" className="px-3 py-3">
                        <p className="text-sm font-medium text-gray-800 mb-1">Error</p>
                        <p className="text-sm text-red-700 mb-3 whitespace-pre-wrap break-words">
                          {row.errorMessage || "—"}
                        </p>
                        {row.errorStack && (
                          <>
                            <p className="text-sm font-medium text-gray-800 mb-1">Stack trace</p>
                            <pre className="text-xs text-gray-700 bg-white border border-gray-200 rounded p-2 overflow-x-auto max-h-80">
                              {row.errorStack}
                            </pre>
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">
          Page {page} of {totalPages} ({result?.total ?? 0} entries)
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
            className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default CronLogs;
